import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { lazy, Suspense } from 'react';

const LoginComponent = lazy(() => import('./new-login'));
const ForgotPassword = lazy(() => import('./new-forgot-password'));
const ResetPasswordForm = lazy(() => import('./new-reset-form'));
const Dashboard = lazy(() => import('./dashboard'));
const Nopage = lazy(() => import('./nopage'));
const LoadingDisplay = lazy(() => import('./loader'));
const SessionWarning = lazy(() => import('./session-warning'));

const SESSION_DURATION = 12 * 60 * 60 * 1000; // 12 hours
const WARNING_THRESHOLD = 2 * 60 * 1000; // 2 minutes
const VERSION_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes


const ProtectedRoute = React.memo(({ children, isAuthenticated }) => {
  const location = useLocation();
  if (!isAuthenticated) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }
  return children;
});

const PublicRoute = React.memo(({ children, isAuthenticated }) => {
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
});

const useAuth = () => {

  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    const code = localStorage.getItem('code');
    const sessionStartTime = localStorage.getItem('sessionStartTime');
    const versionMismatch = localStorage.getItem('versionMismatch');

    // if (versionMismatch === 'true') {
    //   localStorage.removeItem('code');
    //   localStorage.removeItem('sessionStartTime');
    //   localStorage.removeItem('appVersion');
    //   localStorage.removeItem('versionMismatch');
    //   return false;
    // }

    // const versionMismatch = localStorage.getItem('versionMismatch');

    // if (versionMismatch === 'true') {
    //   localStorage.removeItem('code');
    //   localStorage.removeItem('sessionStartTime');
    //   localStorage.removeItem('appVersion');
    //   localStorage.removeItem('versionMismatch');
    //   return false;
    // }

    if (code && sessionStartTime) {
      const elapsed = Date.now() - parseInt(sessionStartTime);
      if (elapsed < SESSION_DURATION) {
        return true;
      } else {
        localStorage.removeItem('code');
        localStorage.removeItem('sessionStartTime');
        return false;
      }
    }
    return false;
  });

  const [timeRemaining, setTimeRemaining] = useState(0);
  const [showExpiredWarning, setShowExpiredWarning] = useState(false);
  const [showNewVersionWarning, setShowNewVersionWarning] = useState(false); // Add this

  useEffect(() => {
    const checkVersion = async () => {
      try {
        const response = await fetch('/version.json?t=' + Date.now());
        // Check if response is OK and is JSON
        if (!response.ok) {
          console.warn('version.json not found, skipping version check');
          return;
        }
        const data = await response.json();
        const storedVersion = localStorage.getItem('appVersion');
        
        if (storedVersion && storedVersion !== data.version) {
          // New version detected - force logout and reload
          console.info('New version detected. Logging out...');
          localStorage.setItem('versionMismatch', 'true');
          // localStorage.setItem('versionMismatch', 'true');
          setShowNewVersionWarning(true);
          setTimeRemaining(0);
          setShowExpiredWarning(true);
          // setTimeRemaining(0);
          // setShowExpiredWarning(true);
        } else if (!storedVersion) {
          localStorage.setItem('appVersion', data.version);
        }
      } catch (error) {
        console.error('Version check failed:', error);
      }
    };

    if (isAuthenticated) {
      checkVersion(); // Check on mount
      const interval = setInterval(checkVersion, VERSION_CHECK_INTERVAL);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      const sessionStartTime = localStorage.getItem('sessionStartTime');
      if (sessionStartTime) {
        const elapsed = Date.now() - parseInt(sessionStartTime);
        const remaining = SESSION_DURATION - elapsed;
        
        if (remaining <= 0) {
          setTimeRemaining(0);
          setShowExpiredWarning(true);
        } else {
          setTimeRemaining(remaining);
          setShowExpiredWarning(false);
        }
      }
    }
  }, [isAuthenticated]);

  useEffect(() => {
    let timer;
    if (isAuthenticated) {
      timer = setInterval(() => {
        const sessionStartTime = localStorage.getItem('sessionStartTime');
        if (sessionStartTime) {
          const elapsed = Date.now() - parseInt(sessionStartTime, 10);
          const remaining = SESSION_DURATION - elapsed;
          
          // FIX: Only update state when crossing threshold or expiring
          // This prevents re-renders every second
          if (remaining <= 0) {
            setTimeRemaining(0);
            setShowExpiredWarning(true);
          } else if (remaining <= WARNING_THRESHOLD) {
            // Only update when in warning zone (updates every second for countdown)
            setTimeRemaining(remaining);
          }
        }
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [isAuthenticated]);

  useEffect(() => {
    const checkSession = () => {
      if (isAuthenticated) {
        const sessionStartTime = localStorage.getItem('sessionStartTime');
        if (sessionStartTime) {
          const elapsed = Date.now() - parseInt(sessionStartTime, 10);
          if (elapsed > SESSION_DURATION) {
            setTimeRemaining(0);
            setShowExpiredWarning(true);
          }
        }
      }
    };
    
    window.addEventListener('focus', checkSession);
    return () => window.removeEventListener('focus', checkSession);
  }, [isAuthenticated]);

  const handleLoginSuccess = useCallback((code) => {
    if (!code) {
      console.error('No code provided for login');
      return;
    }
    
    const currentTime = Date.now();
    localStorage.setItem('code', code);
    localStorage.setItem('sessionStartTime', currentTime.toString());
    
    setIsAuthenticated(true);
    setTimeRemaining(SESSION_DURATION);
    setShowExpiredWarning(false);
  }, []);

  // ✅ FIX: Wrap in useCallback with empty deps - stable function reference
  const handleLogout = useCallback(() => {
    localStorage.removeItem('code');
    localStorage.removeItem('sessionStartTime');
    localStorage.removeItem('appVersion');

    // localStorage.removeItem('appVersion');

    setIsAuthenticated(false);
    setTimeRemaining(0);
    setShowExpiredWarning(false);
    setShowNewVersionWarning(false);
    // setShowNewVersionWarning(false);
    
  }, []);

  // FIX: Remove isAuthenticated from deps
  const extendSession = useCallback(() => {
    const currentTime = Date.now();
    localStorage.setItem('sessionStartTime', currentTime.toString());
    setTimeRemaining(SESSION_DURATION);
    setShowExpiredWarning(false);
    console.log('Session extended');
  }, []); // ✅ Empty deps = stable function

  return {
    isAuthenticated,
    handleLoginSuccess,
    handleLogout,
    extendSession,
    timeRemaining,
    showExpiredWarning,
    sessionActive: isAuthenticated && timeRemaining > 0,
    showNewVersionWarning, // Add this
  };
};

const Container = () => {
  const { 
    isAuthenticated, 
    handleLoginSuccess, 
    handleLogout, 
    extendSession, 
    timeRemaining,
    
    showNewVersionWarning
  } = useAuth();

  console.log('Container rendered');

  // ✅ FIX: Memoize routes with stable dependencies
  const routes = useMemo(() => [
    {
      path: "/",
      element: (
        <PublicRoute isAuthenticated={isAuthenticated}>
          <LoginComponent onLoginSuccess={handleLoginSuccess} />
        </PublicRoute>
      )
    },
    {
      path: "/forgot-password/",
      element: (
        <PublicRoute isAuthenticated={isAuthenticated}>
          <ForgotPassword onLoginSuccess={handleLoginSuccess} />
        </PublicRoute>
      )
    },
    {
      path: "/reset-password/",
      element: (
        <PublicRoute isAuthenticated={isAuthenticated}>
          <ResetPasswordForm onLoginSuccess={handleLoginSuccess} onLogout={handleLogout}/>
        </PublicRoute>
      )
    },
    {
      path: "/dashboard/*",
      element: (
        <ProtectedRoute isAuthenticated={isAuthenticated}>
          <Dashboard onLogout={handleLogout}/>
        </ProtectedRoute>
      )
    },
    {
      path: "*",
      element: <Nopage />
    }
  ], [isAuthenticated, handleLoginSuccess, handleLogout]);

  const shouldShowWarning = showNewVersionWarning || (isAuthenticated && timeRemaining <= WARNING_THRESHOLD);

  // const shouldShowWarning = showNewVersionWarning || (isAuthenticated && timeRemaining <= WARNING_THRESHOLD);

  return (
    <BrowserRouter>
      <Suspense fallback={<LoadingDisplay />}>
        {shouldShowWarning && (
          <SessionWarning 
            timeRemaining={timeRemaining}
            onExtend={showNewVersionWarning ? () => {} : extendSession}
            onLogout={handleLogout}
            isExpired={showNewVersionWarning}
            expiredMessage={
              showNewVersionWarning 
                ? "A new version has been deployed. Please log in again to access the latest features."
                : "Your login session has expired. Please login again."
            }
          />
        )}
        
        <Routes>
          {routes.map(({ path, element }) => (
            <Route key={path} path={path} element={element} />
          ))}
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
};

export default Container;